/**
 * Pie variation — pie-chart on top showing each participant's share, with
 * the line list below for fine-grained claim toggles. The pie is purely
 * visual; tapping is still done at the line level.
 *
 * Defers to ListVariation for line interactions to avoid divergent claim
 * behaviour across layouts; only the summary header differs.
 */
import { useMemo } from "react";
import { colorFor } from "@/lib/participant-colors";
import { ListVariation } from "./ListVariation";
import type { VariationProps } from "./types";

export function PieVariation(props: VariationProps) {
  const { state, lines, participantById, format, selfId, participants } = props;

  // Per-participant total (lineTotal / N for each line they share).
  const slices = useMemo(() => {
    const totals: Record<string, number> = {};
    let unclaimed = 0;
    for (const ln of lines) {
      const sharers = state.sharers[ln.lineId] ?? [];
      if (sharers.length === 0) {
        unclaimed += ln.totalAmount;
        continue;
      }
      const per = ln.totalAmount / sharers.length;
      for (const s of sharers) {
        totals[s] = (totals[s] ?? 0) + per;
      }
    }
    const grand =
      Object.values(totals).reduce((a, b) => a + b, 0) + unclaimed || 1;
    const ordered = participants.map((p) => ({
      id: p.guestId,
      label: p.guestId === selfId ? "You" : p.displayName,
      amount: totals[p.guestId] ?? 0,
      color: colorFor(p.colorIndex),
    }));
    if (totals[selfId] && !ordered.some((s) => s.id === selfId)) {
      ordered.unshift({
        id: selfId,
        label: "You",
        amount: totals[selfId] ?? 0,
        color: colorFor(0),
      });
    }
    return { slices: ordered, unclaimed, grand };
  }, [lines, state.sharers, selfId, participants]);

  // Render a simple SVG pie. Each slice gets an arc proportional to its share;
  // unclaimed amount is rendered as a dashed outline ring.
  const radius = 70;
  const cx = 80;
  const cy = 80;
  let cursor = -Math.PI / 2;
  const arcs: Array<{ d?: string; isFull?: boolean; fill: string; key: string }> = [];
  for (const s of slices.slices) {
    if (s.amount <= 0) continue;
    const angle = (s.amount / slices.grand) * Math.PI * 2;
    if (angle >= Math.PI * 2 - 0.0001) {
      arcs.push({
        key: s.id,
        isFull: true,
        fill: s.color.strong,
      });
      continue;
    }
    const x1 = cx + radius * Math.cos(cursor);
    const y1 = cy + radius * Math.sin(cursor);
    cursor += angle;
    const x2 = cx + radius * Math.cos(cursor);
    const y2 = cy + radius * Math.sin(cursor);
    const large = angle > Math.PI ? 1 : 0;
    arcs.push({
      key: s.id,
      d: `M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${large} 1 ${x2} ${y2} Z`,
      fill: s.color.strong,
    });
  }

  return (
    <>
      <div
        style={{
          display: "flex",
          gap: 16,
          alignItems: "center",
          padding: "8px 16px 16px",
        }}
      >
        <svg width={160} height={160} aria-hidden>
          <circle cx={cx} cy={cy} r={radius} fill="var(--surface-2)" />
          {arcs.map((a) =>
            a.isFull ? (
              <circle key={a.key} cx={cx} cy={cy} r={radius} fill={a.fill} />
            ) : (
              <path key={a.key} d={a.d!} fill={a.fill} />
            )
          )}
          {slices.unclaimed > 0 && (
            <circle
              cx={cx}
              cy={cy}
              r={radius - 1}
              fill="none"
              stroke="var(--ink-3)"
              strokeWidth={2}
              strokeDasharray="4 4"
            />
          )}
        </svg>
        <div style={{ flex: 1, fontSize: 12 }}>
          {slices.slices
            .filter((s) => s.amount > 0)
            .map((s) => (
              <div
                key={s.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 6,
                }}
              >
                <span
                  aria-hidden
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 999,
                    background: s.color.strong,
                  }}
                />
                <span style={{ flex: 1, color: "var(--ink-1)", fontWeight: 600 }}>
                  {s.label}
                </span>
                <span style={{ color: "var(--ink-2)", fontWeight: 700 }}>
                  {format(s.amount)}
                </span>
              </div>
            ))}
          {slices.unclaimed > 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                color: "var(--ink-3)",
              }}
            >
              <span
                aria-hidden
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 999,
                  border: "1px dashed var(--ink-3)",
                }}
              />
              <span style={{ flex: 1, fontWeight: 600 }}>Unclaimed</span>
              <span style={{ fontWeight: 700 }}>
                {format(slices.unclaimed)}
              </span>
            </div>
          )}
        </div>
        {/* participantById passed through for parity with other variations
            even though the pie pulls from `slices`. */}
        <span aria-hidden style={{ display: "none" }}>
          {participantById.size}
        </span>
      </div>
      <ListVariation {...props} />
    </>
  );
}
